// ThreadView — a thread is a QUEUE item that you READ (spec §1 / §5 rule 8).
//
// A thread reads as a conversation with a subject and a resolve control. The
// composer sits AFTER the last message (in-flow, not pinned) with Resolve +
// Assign beside it — the only place those controls appear (fn_thread_resolve /
// fn_thread_assign refuse on channels and DMs). The thread summary renders via
// <Sentence> (verbatim, never rebuilt). work[] and commitments[] render below the
// messages. Paginates backwards with p_before (never fetch a whole thread).
import React, { useCallback, useEffect, useState, useRef } from "react";
import { readConversation } from "../../../api/conversations";
import { Composer, Sentence } from "../kit";
import { EmptyState, ErrorState } from "../../shared/StateMessage";
import PanelErrorBoundary from "../../shared/PanelErrorBoundary";
import MessageRow from "./MessageRow";
import ThreadActions from "./ThreadActions";
import SendHealthNotice from "./SendHealthNotice";
import RoutingSuggestion from "./RoutingSuggestion";
import { ConversationsReadingSkeleton } from "./ConversationsSkeleton";
import { formatWhen, formatDue } from "../../shared/format";
import { useScrollBottom } from "./useScrollBottom";
import { useInfiniteScrollTop } from "./useInfiniteScroll";
import { prependOlder, pageExhausted } from "./mergeMessages";

const PAGE_SIZE = 60;
const CLIENT_BORDER = "border-l-4 border-l-[#32B1FF]";

/**
 * @param {object} props
 * @param {object} props.thread   { id, subject, summary, client_facing, client_id, assigned_to, assigned, ... } from the rail's threads data
 * @param {(threadId: string, resolved: boolean) => void} [props.onResolved]
 *   Passed through to <ThreadActions>; the page uses it to sync the rail.
 * @param {(threadId: string, member: { id: string, name: string }) => void} [props.onAssigned]
 *   Passed through to <ThreadActions>; the page uses it to sync the rail.
 * @param {Set<string>} [props.existingTerms]
 *   Epic 2 — the page-level set of `term`s that already have a routing rule
 *   (from getRoutingRules). Passed down to <RoutingSuggestion> so it hides the
 *   Accept button for terms whose rule is already learned.
 * @param {(term: string) => void} [props.onRoutingRuleAccepted]
 *   Epic 2 — notified by <RoutingSuggestion> after a rule is accepted, with the
 *   `term`. The page adds it to the `existingTerms` set so the button stays
 *   hidden for the rest of the session (optimistic — no re-fetch needed).
 */
export default function ThreadView({ thread, onResolved, onAssigned, existingTerms, onRoutingRuleAccepted }) {
  const t = thread || {};
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  // Synchronous in-flight guard closed BEFORE the await so a scroll burst can't
  // fire loadMore twice and reuse the same stale cursor.
  const loadingMoreRef = useRef(false);

  // Pin the messages scroll container to the bottom on open and after a send.
  // scrollTop=scrollHeight (not scrollIntoView) — the hub has nested
  // overflow-y-auto containers, so a sentinel races the wrong one.
  const { ref: scrollRef, scrollToBottomAfterPaint } = useScrollBottom();

  const fetchThread = useCallback(async () => {
    const res = await readConversation({ type: "thread", id: t.id, limit: PAGE_SIZE });
    const d = res || {};
    setData(d);
    setMessages(Array.isArray(d.messages) ? d.messages : []);
    setNextBefore(d.next_before || null);
    setTotalCount(typeof d.thread?.messages === "number" ? d.thread.messages : null);
    setResolved(d.thread?.resolved ?? false);
    setExhausted(false);
    return d;
  }, [t.id]);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      await fetchThread();
      setStatus("ready");
    } catch (err) {
      setStatus("error");
    }
  }, [fetchThread]);

  useEffect(() => { load(); }, [load]);

  // Opening a thread (or switching threads) jumps to the newest message.
  useEffect(() => {
    if (status !== "ready") return;
    scrollToBottomAfterPaint();
  }, [status, t.id, scrollToBottomAfterPaint]);

  // After a send: re-fetch (no loading skeleton — keeps the reader's place) and
  // jump to the just-sent message. Silent on error so a failed refresh never
  // wipes the list the user is already looking at.
  const refresh = useCallback(async () => {
    try { await fetchThread(); } catch { /* silent */ }
    scrollToBottomAfterPaint();
  }, [fetchThread, scrollToBottomAfterPaint]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    // Cursor: prefer next_before if the backend returns it; otherwise use the
    // oldest loaded message's `at` timestamp (messages are ascending — [0] is
    // oldest). fn_conversation_read does NOT return next_before, so this is the
    // primary path in production.
    const cursor = nextBefore || messages[0]?.at;
    if (!cursor) return;
    // Close the re-entrancy window synchronously, before the await, so a scroll
    // burst can't fire loadMore twice with the same stale cursor.
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await readConversation({ type: "thread", id: t.id, limit: PAGE_SIZE, before: cursor });
      const d = res || {};
      const older = Array.isArray(d.messages) ? d.messages : [];
      // Dedup by id + sort ascending by `at` — no duplicates, no out-of-order
      // dates, even when the server returns overlapping/inclusive pages.
      setMessages((prev) => prependOlder(prev, older));
      setNextBefore(d.next_before || null);
      // Stop paging when a page brings nothing strictly older than the cursor.
      if (pageExhausted(older, cursor)) setExhausted(true);
    } catch (err) { /* silent */ }
    finally { loadingMoreRef.current = false; setLoadingMore(false); }
  }, [t.id, nextBefore, messages]);

  // hasMore: the backend does NOT return next_before, so we derive it from the
  // total message count (thread.messages) vs how many we've loaded. Honor
  // `exhausted` so a page that brought nothing strictly older stops paging.
  const hasMore = !exhausted && ((typeof totalCount === "number" && totalCount > messages.length) || !!nextBefore);

  // Infinite scroll upwards: when the top sentinel enters view, fetch the next
  // (older) page and anchor the scroll position so the reader isn't yanked.
  const { sentinelRef } = useInfiniteScrollTop({
    scrollRef,
    hasMore,
    loading: loadingMore,
    ready: status === "ready",
    onLoadMore: loadMore,
  });

  const handleResolved = useCallback(
    (id, next) => {
      setResolved(next);
      onResolved?.(id, next);
    },
    [onResolved],
  );

  if (status === "loading") {
    return <ConversationsReadingSkeleton />;
  }
  if (status === "error") {
    return (
      <div className="p-4">
        <ErrorState variant="inset" message="This thread is temporarily unavailable. Please try again shortly." onRetry={load} />
      </div>
    );
  }
  const th = data?.thread || t || {};
  const commitments = Array.isArray(data?.commitments) ? data.commitments : [];
  const work = Array.isArray(data?.work) ? data.work : [];

  return (
    <div className="flex h-full flex-col" data-testid="thread-view">
      <header
        className={`border-b border-gray-200 px-4 py-3 dark:border-gray-800 ${th.client_facing ? CLIENT_BORDER : "border-l-4 border-l-transparent"}`}
      >
        <div className="flex items-center gap-2">
          {th.client_facing && (
            <span className="shrink-0 rounded bg-[#32B1FF]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#32B1FF]">
              Client
            </span>
          )}
          <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {th.subject || "(no subject)"}
          </h2>
          {resolved && (
            <span className="shrink-0 rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              Resolved
            </span>
          )}
        </div>
        {th.summary && (
          <Sentence says={th.summary} tint="cool" size="sm" className="mt-1.5" />
        )}
      </header>

      <PanelErrorBoundary label="thread-view-body">
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-testid="thread-scroll">
          {hasMore && !loadingMore && (
            <div className="mb-3 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-gray-800"
                data-testid="stream-load-more"
              >
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15" />
                </svg>
                Load older messages
              </button>
            </div>
          )}
          {loadingMore && (
            <div className="mb-3 flex items-center justify-center gap-2" data-testid="stream-loading-more">
              <svg className="h-4 w-4 animate-spin text-[#32B1FF]" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-xs text-gray-500 dark:text-gray-400">Loading older messages…</span>
            </div>
          )}
          {messages.length === 0 ? (
            <EmptyState variant="inset" message="No messages yet." padding="py-8" />
          ) : (
            <div className="space-y-1.5">
              {messages.map((m) => (
                <MessageRow key={m.id} message={m} />
              ))}
            </div>
          )}

          {(commitments.length > 0 || work.length > 0) && (
            <div className="mt-4 space-y-2 border-t border-gray-100 pt-3 dark:border-gray-800/50" data-testid="thread-commitments">
              {commitments.map((c) => (
                <div key={c.id} className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/50">
                  <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${c.met ? "bg-emerald-500" : "bg-amber-500"}`} aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <Sentence says={c.says} tint={c.met ? "positive" : "cool"} size="sm" />
                    {c.due_at && (
                      <p className="mt-0.5 text-xs text-gray-400">
                        {c.met ? "Done" : "Due"} {formatDue({ due: c.due_at })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {work.map((w) => (
                <div key={w.id} className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800/50">
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-gray-400" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <Sentence says={w.says || w.summary} tint="cool" size="sm" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PanelErrorBoundary>

      <div className="shrink-0 border-t border-gray-200 p-3 dark:border-gray-800">
        <div className="mb-2">
          <ThreadActions
            thread={{
              id: t.id,
              resolved,
              client_facing: th.client_facing,
              assigned_to: t.assigned_to,
              assigned: t.assigned,
            }}
            onResolved={handleResolved}
            onAssigned={onAssigned}
          />
        </div>
        {/* Epic C4 — composer-area notices, above the client-facing wall so they
            are seen BEFORE typing. SendHealthNotice = "sending is off" (default
            off; fetched here, not in the page Promise.all, because v_send_health
            times out and must not block the thread load). RoutingSuggestion =
            "who should answer this", only when the thread has no owner. */}
        <SendHealthNotice />
        <RoutingSuggestion
          thread={t}
          messageId={messages.length ? messages[messages.length - 1].id : null}
          existingTerms={existingTerms}
          onAccepted={(rule) => onRoutingRuleAccepted?.(rule?.term)}
        />
        {/* Client-facing wall (Epic C3): a marker + "who can see this" line
            around <Composer>. The wall is enforced server-side; the missing
            p_client_facing toggle INSIDE <Composer> is a kit gap for Charles —
            never fork the kit. We only mark the surface. */}
        <div
          className={
            th.client_facing ? `${CLIENT_BORDER} pl-2` : ""
          }
          data-testid="composer-wall"
        >
          {th.client_facing && (
            <div className="mb-1.5 flex items-center gap-1.5 text-xs">
              <span className="font-medium text-[#32B1FF]">Client-facing</span>
              <span className="text-gray-400 dark:text-gray-500">
                — the client can see this thread
              </span>
            </div>
          )}
          <Composer channel="email" threadId={t.id} clientId={th.client_id} onSent={refresh} />
        </div>
      </div>
    </div>
  );
}
